import React, { useEffect, useRef, useState } from "react";
import { Pin, Trash2 } from "lucide-react";
import {
  BOARD_DELETE_LABEL,
  BOARD_PIN_LABEL,
  BOARD_SWIPE_DELETE_HINT,
  BOARD_UNPIN_LABEL,
} from "../../lib/eventBoard/constants";
import LinkifiedText from "../LinkifiedText";

const DELETE_REVEAL_PX = 72;
const SWIPE_OPEN_THRESHOLD = DELETE_REVEAL_PX / 2;

const PIN_BTN =
  "inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md2 text-ink/55 hover:text-ink disabled:opacity-40 touch-manipulation focus-visible:outline focus-visible:outline-2 focus-visible:outline-plum/45 focus-visible:outline-offset-2";

function formatRelativeTime(iso) {
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function EventBoardMessage({
  message,
  isHosting,
  deleteRevealed,
  onDeleteRevealChange,
  onDelete,
  onPin,
  deleting,
  pinning,
}) {
  const canDelete = message.is_mine;
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchRef = useRef({ x: 0, openAtStart: false });
  const dragXRef = useRef(0);
  const movedRef = useRef(false);
  const hintId = `board-delete-hint-${message.id}`;

  const settledX = deleteRevealed ? -DELETE_REVEAL_PX : 0;
  const translateX = isDragging ? dragX : settledX;

  useEffect(() => {
    if (!isDragging) {
      setDragOffset(deleteRevealed ? -DELETE_REVEAL_PX : 0);
    }
  }, [deleteRevealed, isDragging]);

  function clampOffset(value) {
    return Math.min(0, Math.max(-DELETE_REVEAL_PX, value));
  }

  function setDragOffset(value) {
    dragXRef.current = value;
    setDragX(value);
  }

  function settleOffset(offset) {
    const open = offset <= -SWIPE_OPEN_THRESHOLD;
    onDeleteRevealChange?.(open);
    setDragOffset(open ? -DELETE_REVEAL_PX : 0);
  }

  function handleTouchStart(e) {
    if (!canDelete) return;
    touchRef.current = { x: e.touches[0].clientX, openAtStart: deleteRevealed };
    movedRef.current = false;
    setIsDragging(true);
    setDragOffset(deleteRevealed ? -DELETE_REVEAL_PX : 0);
  }

  function handleTouchMove(e) {
    if (!canDelete || !isDragging) return;
    const dx = e.touches[0].clientX - touchRef.current.x;
    if (Math.abs(dx) > 6) movedRef.current = true;
    const base = touchRef.current.openAtStart ? -DELETE_REVEAL_PX : 0;
    setDragOffset(clampOffset(base + dx));
  }

  function handleTouchEnd() {
    if (!canDelete) return;
    setIsDragging(false);
    settleOffset(dragXRef.current);
    window.setTimeout(() => {
      movedRef.current = false;
    }, 50);
  }

  function handleCardClick(e) {
    if (!canDelete || movedRef.current) return;
    if (e.target.closest("button")) return;
    onDeleteRevealChange?.(!deleteRevealed);
  }

  function handleDeleteClick(e) {
    e.stopPropagation();
    onDelete(message.id);
  }

  const rowClass = [
    "relative z-10 py-3 px-1 bg-white motion-reduce:transition-none transition-transform duration-200 ease-out",
    message.is_host_message ? "bg-plum/[0.06]" : "",
    message.is_pinned ? "bg-accent/[0.06]" : "",
    canDelete ? "cursor-pointer touch-pan-y" : "",
  ].filter(Boolean).join(" ");

  return (
    <div
      className="relative overflow-hidden border-b border-ink/10 last:border-b-0"
      data-testid={`event-board-message-${message.id}`}
    >
      {canDelete && (
        <div
          className={`absolute inset-y-0 right-0 w-[72px] flex items-center justify-center bg-red-700 ${
            deleteRevealed ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={!deleteRevealed}
        >
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={deleting}
            aria-label={deleting ? "Deleting…" : BOARD_DELETE_LABEL}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-white disabled:opacity-50 touch-manipulation"
            data-testid={`event-board-delete-${message.id}`}
          >
            <Trash2 className="w-5 h-5" strokeWidth={2.5} aria-hidden />
          </button>
        </div>
      )}

      <article
        className={rowClass}
        style={{ transform: `translateX(${translateX}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onClick={handleCardClick}
        role={canDelete ? "button" : undefined}
        tabIndex={canDelete ? 0 : undefined}
        onKeyDown={canDelete ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onDeleteRevealChange?.(!deleteRevealed);
          }
        } : undefined}
        aria-expanded={canDelete ? deleteRevealed : undefined}
        aria-describedby={canDelete ? hintId : undefined}
        aria-busy={deleting || pinning}
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-ui font-semibold text-[15px] text-ink leading-snug">
              <span>{message.author?.name || "Dancer"}</span>
              <span className="text-ink/50 font-medium"> · </span>
              <time className="text-[13px] font-medium text-ink/55 tabular-nums" dateTime={message.created_at}>
                {formatRelativeTime(message.created_at)}
              </time>
            </p>
            <p className="mt-1 text-[15px] font-medium text-ink leading-relaxed whitespace-pre-wrap break-words">
              <LinkifiedText>{message.content}</LinkifiedText>
            </p>
          </div>
          {isHosting && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPin(message.id, !message.is_pinned);
              }}
              disabled={pinning}
              aria-pressed={message.is_pinned}
              aria-label={message.is_pinned ? BOARD_UNPIN_LABEL : BOARD_PIN_LABEL}
              className={`${PIN_BTN} shrink-0 ${message.is_pinned ? "text-plum" : ""}`}
              data-testid={`event-board-pin-${message.id}`}
            >
              <Pin
                className="w-4 h-4"
                strokeWidth={2.5}
                fill={message.is_pinned ? "currentColor" : "none"}
                aria-hidden
              />
            </button>
          )}
        </div>
        {canDelete && (
          <p id={hintId} className="sr-only">{BOARD_SWIPE_DELETE_HINT}</p>
        )}
      </article>
    </div>
  );
}
